import React, { useEffect, useState } from "react";
import {
  Drawer,
  Box,
  Typography,
  IconButton,
  Stack,
  TextField,
  Autocomplete,
  Chip,
  Button,
  Divider,
  MenuItem,
  ToggleButton,
  ToggleButtonGroup,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import FilterAltIcon from "@mui/icons-material/FilterAlt";
import { api } from "../../utils/api";

const BUSINESS_TYPES = [
  "AOP (Association of Persons)",
  "BOI (Body of Individuals)",
  "Co-operative Society",
  "Educational Institution",
  "Government Department",
  "HUF (Hindu Undivided Family)",
  "Hospital / Healthcare",
  "Joint Venture",
  "LLP",
  "Local Authority",
  "MSME",
  "NGO",
  "OPC",
  "Partnership",
  "Proprietorship",
  "Public Ltd",
  "Pvt Ltd",
  "Real Estate / Builder",
  "Section 8 Company",
  "Society",
  "Startup",
  "Statutory Body",
  "Trust",
  "Unregistered",
];

const GRADES = ["A+", "A", "B", "C", "D"];

const SORT_OPTIONS = [
  { value: "id", label: "Newest First" },
  { value: "business_name", label: "Business Name" },
  { value: "grade", label: "Grade" },
  { value: "created_at", label: "Created Date" },
  { value: "state", label: "State" },
  { value: "city", label: "City" },
];

export const emptyFilters = {
  search: "",
  gst: "",
  pan: "",
  mobile: "",
  email: "",
  pincode: "",
  grade: [],
  business: [],
  state: [],
  city: [],
  has_gst: "",
  has_email: "",
  has_mobile: "",
  created_from: "",
  created_to: "",
  sort_by: "id",
  sort_order: "desc",
};

export default function ClientFilterDrawer({ open, onClose, filters, onApply }) {
  const [local, setLocal] = useState(filters || emptyFilters);
  const [options, setOptions] = useState({ states: [], cities: [] });

  useEffect(() => {
    if (open) setLocal(filters || emptyFilters);
  }, [open, filters]);

  useEffect(() => {
    if (!open) return;
    (async () => {
      try {
        const res = await api.get("/clients/filter_options.php");
        setOptions({
          states: res.states || [],
          cities: res.cities || [],
        });
      } catch (e) {
        // non-fatal
      }
    })();
  }, [open]);

  const set = (k, v) => setLocal((p) => ({ ...p, [k]: v }));

  const handleReset = () => {
    setLocal(emptyFilters);
  };

  const handleApply = () => {
    onApply(local);
    onClose();
  };

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{ sx: { width: { xs: "100%", sm: 420 } } }}
    >
      <Box sx={{ display: "flex", flexDirection: "column", height: "100%" }}>
        {/* HEADER */}
        <Box
          sx={{
            p: 2,
            display: "flex",
            alignItems: "center",
            borderBottom: 1,
            borderColor: "divider",
          }}
        >
          <FilterAltIcon sx={{ mr: 1, color: "primary.main" }} />
          <Typography variant="h6" sx={{ flex: 1, fontWeight: 700 }}>
            Filter Clients
          </Typography>
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>

        {/* BODY */}
        <Box sx={{ flex: 1, overflowY: "auto", p: 2 }}>
          <Stack spacing={2}>
            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700 }}>
              BASIC
            </Typography>

            <TextField
              label="Business / Contact Name"
              size="small"
              value={local.search}
              onChange={(e) => set("search", e.target.value)}
              fullWidth
            />
            <TextField
              label="GSTIN"
              size="small"
              value={local.gst}
              onChange={(e) => set("gst", e.target.value)}
              fullWidth
            />
            <TextField
              label="PAN"
              size="small"
              value={local.pan}
              onChange={(e) => set("pan", e.target.value)}
              fullWidth
            />
            <TextField
              label="Mobile / WhatsApp"
              size="small"
              value={local.mobile}
              onChange={(e) => set("mobile", e.target.value)}
              fullWidth
            />
            <TextField
              label="Email"
              size="small"
              value={local.email}
              onChange={(e) => set("email", e.target.value)}
              fullWidth
            />

            <Divider />
            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700 }}>
              CATEGORY
            </Typography>

            <Autocomplete
              multiple
              size="small"
              options={GRADES}
              value={local.grade}
              onChange={(_e, v) => set("grade", v)}
              renderInput={(p) => <TextField {...p} label="Grade" />}
              renderTags={(val, getTagProps) =>
                val.map((opt, i) => (
                  <Chip label={opt} size="small" {...getTagProps({ index: i })} />
                ))
              }
            />

            <Autocomplete
              multiple
              size="small"
              options={BUSINESS_TYPES}
              value={local.business}
              onChange={(_e, v) => set("business", v)}
              renderInput={(p) => <TextField {...p} label="Business Type" />}
              renderTags={(val, getTagProps) =>
                val.map((opt, i) => (
                  <Chip label={opt} size="small" {...getTagProps({ index: i })} />
                ))
              }
            />

            <Divider />
            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700 }}>
              LOCATION
            </Typography>

            <Autocomplete
              multiple
              freeSolo
              size="small"
              options={options.states}
              value={local.state}
              onChange={(_e, v) => set("state", v)}
              renderInput={(p) => <TextField {...p} label="State" />}
              renderTags={(val, getTagProps) =>
                val.map((opt, i) => (
                  <Chip label={opt} size="small" {...getTagProps({ index: i })} />
                ))
              }
            />

            <Autocomplete
              multiple
              freeSolo
              size="small"
              options={options.cities}
              value={local.city}
              onChange={(_e, v) => set("city", v)}
              renderInput={(p) => <TextField {...p} label="City" />}
              renderTags={(val, getTagProps) =>
                val.map((opt, i) => (
                  <Chip label={opt} size="small" {...getTagProps({ index: i })} />
                ))
              }
            />

            <TextField
              label="Pincode"
              size="small"
              value={local.pincode}
              onChange={(e) => set("pincode", e.target.value)}
              fullWidth
            />

            <Divider />
            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700 }}>
              DATA QUALITY
            </Typography>

            <Box>
              <Typography variant="body2" sx={{ mb: 0.5 }}>Has GSTIN</Typography>
              <ToggleButtonGroup
                size="small"
                exclusive
                value={local.has_gst}
                onChange={(_e, v) => set("has_gst", v || "")}
                fullWidth
              >
                <ToggleButton value="">Any</ToggleButton>
                <ToggleButton value="yes">Yes</ToggleButton>
                <ToggleButton value="no">No</ToggleButton>
              </ToggleButtonGroup>
            </Box>

            <Box>
              <Typography variant="body2" sx={{ mb: 0.5 }}>Has Email</Typography>
              <ToggleButtonGroup
                size="small"
                exclusive
                value={local.has_email}
                onChange={(_e, v) => set("has_email", v || "")}
                fullWidth
              >
                <ToggleButton value="">Any</ToggleButton>
                <ToggleButton value="yes">Yes</ToggleButton>
                <ToggleButton value="no">No</ToggleButton>
              </ToggleButtonGroup>
            </Box>

            <Box>
              <Typography variant="body2" sx={{ mb: 0.5 }}>Has Mobile</Typography>
              <ToggleButtonGroup
                size="small"
                exclusive
                value={local.has_mobile}
                onChange={(_e, v) => set("has_mobile", v || "")}
                fullWidth
              >
                <ToggleButton value="">Any</ToggleButton>
                <ToggleButton value="yes">Yes</ToggleButton>
                <ToggleButton value="no">No</ToggleButton>
              </ToggleButtonGroup>
            </Box>

            <Divider />
            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700 }}>
              DATE & SORT
            </Typography>

            <Stack direction="row" spacing={1}>
              <TextField
                label="Created From"
                type="date"
                size="small"
                value={local.created_from}
                onChange={(e) => set("created_from", e.target.value)}
                InputLabelProps={{ shrink: true }}
                fullWidth
              />
              <TextField
                label="Created To"
                type="date"
                size="small"
                value={local.created_to}
                onChange={(e) => set("created_to", e.target.value)}
                InputLabelProps={{ shrink: true }}
                fullWidth
              />
            </Stack>

            <Stack direction="row" spacing={1}>
              <TextField
                select
                label="Sort By"
                size="small"
                value={local.sort_by}
                onChange={(e) => set("sort_by", e.target.value)}
                fullWidth
              >
                {SORT_OPTIONS.map((o) => (
                  <MenuItem key={o.value} value={o.value}>
                    {o.label}
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                select
                label="Order"
                size="small"
                value={local.sort_order}
                onChange={(e) => set("sort_order", e.target.value)}
                sx={{ minWidth: 110 }}
              >
                <MenuItem value="desc">Desc</MenuItem>
                <MenuItem value="asc">Asc</MenuItem>
              </TextField>
            </Stack>
          </Stack>
        </Box>

        {/* FOOTER */}
        <Box
          sx={{
            p: 2,
            borderTop: 1,
            borderColor: "divider",
            display: "flex",
            gap: 1,
          }}
        >
          <Button
            variant="outlined"
            color="inherit"
            startIcon={<RestartAltIcon />}
            onClick={handleReset}
          >
            Reset
          </Button>
          <Button variant="contained" fullWidth onClick={handleApply}>
            Apply Filters
          </Button>
        </Box>
      </Box>
    </Drawer>
  );
}
